import { useEffect, useMemo } from "react"
import { create } from "zustand"

import type { StrategySectionKey } from "~/components/demand-2/shared/StrategySummaryRail"

import {
  type PreorderInsight,
  preorderRowFigures,
  useFashionPreorderDevelopment,
  usePreorderDevelopment,
} from "~/components/demand-2/shared/preorderDevelopment"
import {
  foldInsightOffers,
  insightLabelFor,
  type InsightFold,
  type NoosMetricKey,
} from "~/components/demand-2/shared/noosBreakdown"
import {
  useCollectionInsights,
  useDemandAdjustments,
  useSeasonSalesTrend,
  useNoosInclusions,
} from "~/components/demand-2/useDemandIntents"
import {
  aggregateImpacts,
  toSetupImpacts,
  type AdjustmentImpact,
} from "~/components/demand-2/shared/adjustmentImpacts"
import { useScopeLabel } from "~/components/demand-2/shared/useScopeLabel"
import { useFramingSignals } from "~/components/demand-2/shared/useFramingSignals"
import { WHOLE_SCOPE } from "~/utils/demandFraming"
import { framingSignalName } from "~/utils/demandFramingSignal"
import { useDraftAdjMap } from "~/components/demand-2/demand/useEffectiveDemand"
import { useDemand2Scope } from "~/components/demand-2/useDemand2Scope"
import type { EffectiveDemand } from "~/utils/demandEffective"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import {
  INSIGHT_KEY_ORDER,
  type InsightOffer,
  type InsightScalars,
  isStrategicInsightKey,
} from "~/utils/demandInsightValues"
import { plannedDemand } from "~/utils/lifecycleUtils"

export type SetupSummaryInput = {
  collectionId: number
  kind: "fashion" | "noos"
  variants: IVariantSummary[]
  /** The reconstruction the Total table reads, so both state one number. */
  effective: (v: IVariantSummary) => EffectiveDemand
  offersFor: (variantId: string) => InsightOffer[] | null
  scalarsFor: (variantId: string) => InsightScalars | null
  /** `getDemandTotals` in flight. */
  insightsLoading: boolean
}

/** Level rows carry `levelUnits`; `contributions` feeds the inspector's where-it-lands view. */
export type InsightRow = InsightFold & {
  /** Only the units cell waits. */
  unitsLoading?: boolean
  /** Replaces the shared level note. */
  note?: string
  /** False: the row states what it would need instead of a figure. */
  available: boolean
}

/**
 * Keys with a switch on this page, per kind. Narrows the SWITCHES only: the engine and totals read
 * every key, which applies from variant data or a decision made elsewhere. Rows
 * render in `INSIGHT_KEY_ORDER`.
 *
 * `preOrderChange` is the other answer to `preorderDevelopment`, picked in its adaptation control.
 */
export const SETTINGS_INSIGHT_KEYS: Record<"fashion" | "noos", ReadonlySet<NoosMetricKey>> = {
  // Fashion's cards plus phasing out; NOOS has no growth card.
  noos: new Set<NoosMetricKey>(["preorderDevelopment", "stockout", "discounts", "phasingOut"]),
  // `seasonTrend` has its own card (`GrowthStrategyCard`); see `STRATEGIC_INSIGHT_KEYS`.
  fashion: new Set<NoosMetricKey>(["preorderDevelopment", "stockout", "discounts"]),
}

export type SetupSummary = {
  collectionId: number
  /** One on a collection route, the members on a group route. */
  collectionIds: number[]
  variants: IVariantSummary[]
  /** The engine reconstruction per variant, for readings the stage totals don't split out. */
  effective: SetupSummaryInput["effective"]
  /** 0 for an unknown id. */
  baselineFor: (variantId: string) => number
  rows: InsightRow[]
  /** {@link SETTINGS_INSIGHT_KEYS} for this kind, in canonical order. */
  configurableRows: InsightRow[]
  scalarsFor: (variantId: string) => InsightScalars | null
  /** In the total with no switch here; the panel names them so no figure goes unexplained. */
  appliedElsewhere: InsightRow[]
  /** Enabled, available and engine-sized: the rows the total is made of. */
  appliedRows: InsightRow[]
  /**
   * The planner's decisions (`STRATEGIC_INSIGHT_KEYS`). Always counted with Strategic assumptions,
   * never with Model insights.
   */
  strategicRows: InsightRow[]
  enabledRows: InsightRow[]
  /** The planner's setup-stage adjustments, one line per logical row, with engine units. */
  signalRows: AdjustmentImpact[]
  baselineTotal: number
  /**
   * The Total table's columns summed over scope, read off the engine reconstruction.
   * `Signal based demand + Manual forecast + Campaigns = Total demand`; see `totalColumns.tsx`.
   */
  stages: {
    baseline: number
    /** No override, adjustments or insights; the Demand signals delta measures from here. */
    model: number
    /** Demand signals column = this − `model`. */
    setupForecast: number
    /** `manualForecast`. */
    manual: number
    /** `totalDemand`. */
    total: number
  }
  levelName: string
  kind: "fashion" | "noos"
  /** A level-scoped insight can only be authored here when this scope owns the whole level. */
  levelAuthorable: boolean
  sizing: boolean
  preorder: PreorderInsight
  seasonTrend: ReturnType<typeof useSeasonSalesTrend>
  collection: ReturnType<typeof useCollectionInsights>
  perVariant: ReturnType<typeof useNoosInclusions>
}

/**
 * One setup-stage computation shared by the page and its inspector. Not context: the panel is
 * registered with `<Module>` and renders above the page. Not two hook calls:
 * `usePreorderDevelopment` owns the adaptation mode, so copies would quote different figures.
 * The page publishes; the panel only reads.
 */
type SetupSummaryStore = {
  summary: SetupSummary | null
  /** One open row per section: a section is a drilldown, not a list. */
  expanded: Partial<Record<StrategySectionKey, NoosMetricKey>>
  publish: (summary: SetupSummary) => void
  setExpanded: (section: StrategySectionKey, key: NoosMetricKey, open: boolean) => void
}

/** Stable identity: an inline `[]` would churn the NOOS read. */
const NO_VARIANTS: string[] = []

export const useSetupSummaryStore = create<SetupSummaryStore>((set) => ({
  summary: null,
  expanded: {},
  publish: (summary) => set({ summary }),
  setExpanded: (section, key, open) =>
    set(({ expanded }) => {
      if (open) return { expanded: { ...expanded, [section]: key } }
      if (expanded[section] !== key) return {}
      return { expanded: { ...expanded, [section]: undefined } }
    }),
}))

/** The engine's offers folded across variants, never re-derived, so switch and Total agree. */
const useOfferedInsights = (
  variants: IVariantSummary[],
  offersFor: (variantId: string) => InsightOffer[] | null,
): Map<NoosMetricKey, InsightRow> =>
  useMemo(
    () =>
      new Map(
        [...foldInsightOffers(variants, offersFor)].map(([key, fold]) => [
          key,
          { ...fold, available: true },
        ]),
      ),
    [variants, offersFor],
  )

/** Computes the setup stage and publishes it for the inspector. Called once, by the Settings page. */
export const usePublishSetupSummary = ({
  collectionId,
  kind,
  variants,
  effective,
  offersFor,
  scalarsFor,
  insightsLoading,
}: SetupSummaryInput): SetupSummary => {
  const { isGroup, authorable, collectionIds } = useDemand2Scope()
  const settingsKeys = SETTINGS_INSIGHT_KEYS[kind]
  const seasonTrend = useSeasonSalesTrend()
  const offered = useOfferedInsights(variants, offersFor)
  const collection = useCollectionInsights()
  const perVariant = useNoosInclusions(collectionId)
  const [adjustments] = useDemandAdjustments()
  const adjByVariant = useDraftAdjMap(adjustments)
  const scopeLabelFor = useScopeLabel()
  const { forVariant: framingFor } = useFramingSignals()
  // Same builder as the grid's setup hover, aggregated like a group row. Never re-sized here: a
  // second sizing path would disagree with the Total column.
  const signalRows = useMemo(
    () =>
      aggregateImpacts(
        variants.map((v) => {
          const adjs = (adjByVariant.get(v.variantId) ?? []).filter((a) => a.stage === "setup")
          // The framing signal has no stored row, so it is seeded from the signal read.
          const framing = framingFor(v.variantId).map((s) => ({
            id: s.id,
            name: framingSignalName(s),
            pct: s.pct,
          }))
          return toSetupImpacts([], adjs, effective(v).setupAttribution, scopeLabelFor, framing)
        }),
      ),
    [variants, adjByVariant, effective, scopeLabelFor, framingFor],
  )

  // Unsized keys are absent from `offered`; while the reads land, enabled keys keep their box and
  // spin instead of appearing one by one.
  const sizing = insightsLoading || seasonTrend.isLoading
  const variantIds = useMemo(() => variants.map((v) => v.variantId), [variants])
  // Both hooks are called; each gates its own read on the kind.
  const noosPreorder = usePreorderDevelopment(
    kind === "noos" ? variantIds : NO_VARIANTS,
    scalarsFor,
    WHOLE_SCOPE,
    insightsLoading,
  )
  const fashionPreorder = useFashionPreorderDevelopment(collectionIds, kind === "fashion")
  const preorder: PreorderInsight = kind === "fashion" ? fashionPreorder : noosPreorder
  // A level-scoped setting is authored at the plan's own level, as publish and
  // `assertWholeGroupsFor` require.
  const levelAuthorable = authorable
  const levelName = isGroup ? "group" : "collection"

  const preorderRow: InsightRow = useMemo(() => {
    const figures = preorderRowFigures(preorder)
    // Synthesised here: the engine's row supplies the units, the order-book read availability.
    const engine = offered.get("preorderDevelopment")
    return {
      key: "preorderDevelopment",
      label: insightLabelFor("preorderDevelopment"),
      // Zero, not the client figure: the order-book read covers the whole plan, so quoting it would
      // list units the subtotal never carries.
      units: engine?.units ?? 0,
      levelUnits: engine?.levelUnits,
      variantCount: engine?.variantCount ?? variants.length,
      contributions: engine?.contributions ?? [],
      unitsLoading: preorder.isLoading || sizing,
      available: preorder.isLoading || figures.hasBasis,
    }
  }, [preorder, variants.length, offered, sizing])

  // EVERY supported key, whether or not it sizes here; an unavailable row states what it needs
  // (`insightRequirementFor`).
  const rows: InsightRow[] = useMemo(
    () =>
      INSIGHT_KEY_ORDER.map((key) => {
        if (key === "preorderDevelopment") return preorderRow
        return (
          offered.get(key) ?? {
            key,
            label: insightLabelFor(key),
            units: 0,
            variantCount: 0,
            contributions: [],
            // While sizing, absence means "not read yet"; unavailable would flash a stale reason.
            available: sizing,
          }
        )
      }),
    [preorderRow, offered, sizing],
  )

  // The Total table sums the same entries, so the chain reconciles to its columns.
  const stages = useMemo(() => {
    const acc = {
      baseline: 0,
      model: 0,
      setupForecast: 0,
      manual: 0,
      total: 0,
      baselineByVariant: new Map<string, number>(),
    }
    for (const v of variants) {
      const e = effective(v)
      acc.baseline += e.baseline
      acc.baselineByVariant.set(v.variantId, e.baseline)
      acc.model += e.model
      acc.setupForecast += e.setupForecast
      // Only `manual` and `total` take the phase-out rule, as their columns do. Manual
      // forecast and Campaigns are residuals (`setupWaterfall`), so guarding one side only would land
      // the phase-out there.
      acc.manual += plannedDemand(v, e.manualForecast)
      acc.total += plannedDemand(v, e.totalDemand)
    }
    return acc
  }, [variants, effective])
  const baselineTotal = stages.baseline

  const summary: SetupSummary = useMemo(() => {
    // On AND available (a key can be on from before the data changed).
    const enabledRows = rows.filter((r) => collection.enabled.has(r.key) && r.available)
    const appliedRows = enabledRows
    const { baselineByVariant, ...stageTotals } = stages
    return {
      collectionId,
      variants,
      effective,
      baselineFor: (variantId: string) => baselineByVariant.get(variantId) ?? 0,
      rows,
      configurableRows: rows.filter((r) => settingsKeys.has(r.key)),
      scalarsFor,
      // Strategic keys have their own card in the same dialog.
      appliedElsewhere: appliedRows.filter(
        (r) => !settingsKeys.has(r.key) && !isStrategicInsightKey(r.key),
      ),
      appliedRows,
      strategicRows: appliedRows.filter((r) => isStrategicInsightKey(r.key)),
      enabledRows,
      signalRows,
      baselineTotal,
      stages: stageTotals,
      levelName,
      kind,
      levelAuthorable,
      sizing,
      preorder,
      seasonTrend,
      collection,
      perVariant,
      collectionIds,
    }
  }, [
    collectionId,
    variants,
    effective,
    rows,
    settingsKeys,
    scalarsFor,
    signalRows,
    baselineTotal,
    stages,
    levelName,
    levelAuthorable,
    sizing,
    preorder,
    seasonTrend,
    kind,
    collection,
    perVariant,
    collectionIds,
  ])

  const publish = useSetupSummaryStore((s) => s.publish)
  // Cleared on unmount so a panel left mounted across views cannot draw a stale collection.
  useEffect(() => {
    publish(summary)
  }, [publish, summary])
  useEffect(() => () => useSetupSummaryStore.setState({ summary: null, expanded: {} }), [])

  return summary
}
